// BB.3 — Central de pendências do plano.
// Lista itens críticos, atenção e bloqueios com CTA direto ao editor/missão.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, AlertTriangle, Lock, Compass, CheckCircle2 } from "lucide-react";
import { getFullBusinessPlanBundle } from "@/lib/business-plan/editor.functions";
import type { PendingItem } from "@/lib/business-plan/full-plan";
import { track } from "@/hooks/useAnalytics";
import { HomologacaoObserver } from "@/components/business-builder/HomologacaoObserver";

export const Route = createFileRoute("/_authenticated/membros/meu-negocio/pendencias")({
  head: () => ({
    meta: [
      { title: "Pendências do plano — Projeto 101" },
      { name: "description", content: "Lista dos pontos que precisam de atenção para completar seu plano de negócio." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PendenciasPage,
});

function PendenciasPage() {
  const fetchBundle = useServerFn(getFullBusinessPlanBundle);
  const { data, isLoading } = useQuery({
    queryKey: ["full-business-plan"],
    queryFn: () => fetchBundle(),
  });

  if (isLoading) return <div className="mx-auto h-72 max-w-3xl animate-pulse rounded-2xl bg-black/5" />;
  if (!data?.hasProject || !data.fullPlan) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-8 text-center">
        <h1 className="font-display text-xl font-extrabold text-[var(--navy)]">Sem plano ativo</h1>
        <Link to="/membros/negocios" className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">
          Explorar biblioteca <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const items = data.fullPlan.pendingItems;
  const critical = items.filter((i) => i.priority === "critical");
  const warning = items.filter((i) => i.priority === "high");
  const info = items.filter((i) => i.priority === "medium");

  useEffect(() => {
    track("builder_pending_viewed", {
      metadata: { total: items.length, critical: critical.length, warning: warning.length },
    });
  }, [items.length, critical.length, warning.length]);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header className="rounded-2xl border border-black/5 bg-white p-6">
        <Link to="/membros/meu-negocio" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)] hover:text-[var(--navy)]">
          <ArrowLeft size={11} /> Voltar ao plano
        </Link>
        <h1 className="mt-1 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">Pendências do plano</h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          {items.length === 0
            ? "Nenhuma pendência ativa. Seu plano está redondo."
            : `${items.length} item${items.length > 1 ? "s" : ""} a resolver — priorize os críticos primeiro.`}
        </p>
      </header>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-[var(--green)]/20 bg-[var(--green)]/5 p-8 text-center">
          <CheckCircle2 className="mx-auto text-[var(--green)]" size={32} />
          <p className="mt-2 font-display text-lg font-extrabold text-[var(--navy)]">Tudo em dia</p>
          <p className="mt-1 text-sm text-[var(--gray-ink)]">Volte ao plano para revisar ou imprimir o documento.</p>
          <Link to="/membros/meu-negocio/plano" className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">
            Ver plano completo <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <>
          {critical.length > 0 && <Group title="Críticos" items={critical} tone="critical" />}
          {warning.length > 0 && <Group title="Precisa revisar" items={warning} tone="warning" />}
          {info.length > 0 && <Group title="A completar" items={info} tone="info" />}
        </>
      )}
      <HomologacaoObserver sectionKey="pendencias" />
    </div>
  );
}

function Group({ title, items, tone }: { title: string; items: PendingItem[]; tone: "critical" | "warning" | "info" }) {
  const Icon = tone === "critical" ? Lock : tone === "warning" ? AlertTriangle : Compass;
  const cls =
    tone === "critical" ? "border-red-200 bg-red-50 text-red-800" :
    tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" :
    "border-black/10 bg-white text-[var(--navy)]";
  return (
    <section className="rounded-2xl border border-black/5 bg-white p-5">
      <h2 className="font-display text-sm font-extrabold uppercase tracking-wide text-[var(--gray-ink)]">{title} ({items.length})</h2>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${cls}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                <Icon size={12} /> {it.title}
              </div>
              <p className="mt-1 text-sm text-[var(--navy)]">{it.reason}</p>
              {it.impact && <p className="mt-0.5 text-[11px] text-[var(--gray-ink)]">{it.impact}</p>}
            </div>
            <Link to={it.cta.to as any} params={it.cta.params as any}
              className="inline-flex shrink-0 items-center gap-1 self-center rounded-md bg-[var(--navy)] px-3 py-1.5 text-[11px] font-semibold text-white">
              {it.cta.label} <ArrowRight size={11} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
